import { gql } from '@urql/core';
import { createQueryKeys } from '@lukemorales/query-key-factory';

export const abTestsFeatureKeys = createQueryKeys('abTestsFeature', {
  abTestList: ({ site, search, status, order, sort, limit }) => [{ site, search, status, order, sort, limit }],
  abTestDashboardList: ({ site, search, status, order, sort, limit }) => [{ site, search, status, order, sort, limit }],
  abTestHomeRecent: ({ site, limit }) => [{ site, limit }],
  abTestListPaginated: ({ site, goalId, startDate, endDate, presetDate, page, limit }) => [
    { site, goalId, startDate, endDate, presetDate, page, limit },
  ],
  abTestDetail: ({ id, startDate, endDate, presetDate }) => [{ id, startDate, endDate, presetDate }],
  abTestEditorDetail: ({ id }) => [{ id }],
  abTestResultDetail: ({ id }) => [{ id }],
  abTestResultCounts: ({ id, goalId, presetDate, startDate, endDate, order, sort }) => [
    { id, goalId, presetDate, startDate, endDate, order, sort },
  ],
  abTestDistribution: ({ id, goalId, presetDate, startDate, endDate }) => [
    { id, goalId, presetDate, startDate, endDate },
  ],
  abTestStatusCounts: ({ site }) => [{ site }],
});

export const abTestListQuery = ({ site, search, status, order, sort, limit }) => ({
  ...abTestsFeatureKeys.abTestList({ site, search, status, order: { field: order, sort }, limit }),
  useErrorBoundary: false,
  getNextPageParam: (lastPage) => (lastPage.abTestList.hasNextPage ? lastPage.abTestList.nextPageCursor : undefined),
  meta: {
    query: gql`
      query AbTestList(
        $cursor: String
        $limit: Int
        $search: String
        $site: Int
        $status: AbTestStatus
        $order: AbTestListOrder
      ) {
        abTestList(cursor: $cursor, limit: $limit, search: $search, site: $site, status: $status, order: $order) {
          list {
            autoReweight
            bestVariant {
              id
              variantName
              viewVariantUrl
              position
              type
              redirectUrl
              weight
              results {
                conversionRate
                improvementFromControl
              }
              significance
            }
            createdAt
            createdByUserId
            createdByUserName
            devices
            duration
            goal {
              id
              name
              historical
            }
            hasResults
            id
            matchingUrl
            name
            pageUrl
            publishedAt
            recentlyCreated
            scriptInstalled
            siteId
            status
            stoppedAt
            thumbnailUrl
            screenshotUrl
            totalConversions
            totalVisitors
            type
            variantsCount
            significance
            permissions {
              canAdjustWeight
              canViewResults
              canStop
              canShare
              canChangeGoal
              canRename
              canCheckInstallation
              canDelete
              canAddVariants
              canDuplicate
            }
            variants {
              id
              variantName
              viewVariantUrl
              position
              type
              redirectUrl
              weight
              significance
              results {
                conversions
                visitors
                conversionRate
                improvementFromControl
              }
            }
          }
          nextPageCursor
          hasNextPage
        }
      }
    `,
  },
});

export const abTestDashboardListQuery = ({ site, search, status, order, sort, limit }) => ({
  ...abTestsFeatureKeys.abTestDashboardList({ site, search, status, order: { field: order, sort }, limit }),
  useErrorBoundary: false,
  getNextPageParam: (lastPage) =>
    lastPage.abTestDashboardList.hasNextPage ? lastPage.abTestDashboardList.nextPageCursor : undefined,
  meta: {
    query: gql`
      query AbTestDashboardList(
        $cursor: String
        $limit: Int
        $search: String
        $site: Int
        $status: AbTestStatus
        $order: AbTestListOrder
      ) {
        abTestDashboardList(
          cursor: $cursor
          limit: $limit
          search: $search
          site: $site
          status: $status
          order: $order
        ) {
          list {
            autoReweight
            bestVariant {
              id
              variantName
              viewVariantUrl
              position
              type
              redirectUrl
              weight
              results {
                conversionRate
                improvementFromControl
              }
              significance
            }
            createdAt
            createdByUserId
            createdByUserName
            devices
            duration
            goal {
              id
              name
              historical
            }
            hasResults
            id
            matchingUrl
            name
            pageUrl
            publishedAt
            recentlyCreated
            scriptInstalled
            siteId
            status
            stoppedAt
            thumbnailUrl
            screenshotUrl
            totalConversions
            totalVisitors
            type
            variantsCount
            significance
            permissions {
              canAdjustWeight
              canViewResults
              canStop
              canShare
              canChangeGoal
              canRename
              canCheckInstallation
              canDelete
              canAddVariants
              canDuplicate
            }
            variants {
              id
              variantName
              viewVariantUrl
              position
              type
              redirectUrl
              weight
              significance
              results {
                conversions
                visitors
                conversionRate
                improvementFromControl
              }
            }
          }
          nextPageCursor
          hasNextPage
        }
      }
    `,
  },
});

export const abTestHomeRecentQuery = ({ site, limit = 3 }) => ({
  ...abTestsFeatureKeys.abTestHomeRecent({ site, limit }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query AbTestHomeRecent($site: Int, $limit: Int) {
        abTestHomeRecent(site: $site, limit: $limit) {
          id
          name
          status
          bestVariant {
            id
            variantName
            type
            results {
              improvementFromControl
            }
          }
        }
      }
    `,
  },
});

export const abTestStatusCountsQuery = ({ site }) => ({
  ...abTestsFeatureKeys.abTestStatusCounts({ site }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query AbTestStatusCounts($site: Int) {
        abTestStatusCounts(site: $site) {
          total
          running
          stopped
          draft
        }
      }
    `,
  },
});

export const abTestListPaginatedQuery = ({ site, goalId, startDate, endDate, presetDate, page, limit }) => ({
  ...abTestsFeatureKeys.abTestListPaginated({ site, goalId, startDate, endDate, presetDate, page, limit }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query AbTestListPaginated(
        $site: Int
        $goalId: Int
        $startDate: Int
        $endDate: Int
        $presetDate: String
        $page: Int!
        $limit: Int
      ) {
        abTestListPaginated(
          site: $site
          goalId: $goalId
          startDate: $startDate
          endDate: $endDate
          presetDate: $presetDate
          page: $page
          limit: $limit
        ) {
          page
          list {
            id
            duration
            publishedAt
            stoppedAt
            createdAt
            createdByUserName
            name
            pageUrl
            status
            type
            totalConversions
            totalVisitors
            variants {
              id
              redirectUrl
              variantName
              results {
                conversions
              }
            }
            variantsCount
          }
        }
      }
    `,
  },
});

export const abTestDetailQuery = ({ id, presetDate, startDate, endDate }) => ({
  ...abTestsFeatureKeys.abTestDetail({ id, presetDate, startDate, endDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query AbTestDetail($id: Int!, $startDate: Int, $endDate: Int, $presetDate: String) {
        abTestDetail(id: $id, startDate: $startDate, endDate: $endDate, presetDate: $presetDate) {
          autoReweight
          bestVariantId
          createdAt
          createdByUserId
          createdByUserName
          devices
          draft
          duration
          goal {
            id
            name
            historical
          }
          selectedGoal {
            id
            name
            hasValue
          }
          conversionGoals {
            id
            name
          }
          hasResults
          id
          matchingUrl
          name
          notes
          pageUrl
          permissions {
            canAddVariants
            canDuplicate
            canAdjustWeight
            canChangeGoal
            canRename
            canCheckInstallation
            canDelete
            canShare
            canStop
            canViewResults
          }
          publishedAt
          publishedBy {
            name
          }
          recentlyCreated
          scriptInstalled
          siteId
          status
          stoppedAt
          thumbnailUrl
          totalConversions
          totalVisitors
          totalValue
          avgConversionRate
          type
          variants {
            createdAt
            updatedAt
            editorPatch
            redirectUrl
            id
            isRetired
            permissions {
              canDuplicate
              canEdit
              canRetire
              canViewRecordings
              canViewSnapshot
            }
            position
            results {
              conversionRate
              conversions
              value
              hasBestImprovement
              improvementFromControl
              trafficSplit
              visitors
            }
            resultsId
            retiredAt
            snapshotId
            thumbnailUrl
            screenshotUrl
            type
            variantName
            viewVariantUrl
            weight
            significance
            description
          }
          variantsCount
          significance
          originalId
        }
      }
    `,
  },
});

export const abTestEditorDetailQuery = ({ id }) => ({
  ...abTestsFeatureKeys.abTestEditorDetail({ id }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query AbTestEditorDetail($id: Int!) {
        abTestEditorDetail(id: $id) {
          autoReweight
          bestVariantId
          createdAt
          createdByUserId
          createdByUserName
          devices
          draft
          duration
          goal {
            id
            name
            historical
          }
          selectedGoal {
            id
            name
            hasValue
          }
          conversionGoals {
            id
            name
          }
          hasResults
          id
          matchingUrl
          name
          notes
          pageUrl
          permissions {
            canAddVariants
            canDuplicate
            canAdjustWeight
            canChangeGoal
            canRename
            canCheckInstallation
            canDelete
            canShare
            canStop
            canViewResults
          }
          publishedAt
          publishedBy {
            name
          }
          recentlyCreated
          scriptInstalled
          siteId
          status
          stoppedAt
          thumbnailUrl
          totalConversions
          totalVisitors
          totalValue
          avgConversionRate
          type
          variants {
            createdAt
            updatedAt
            editorPatch
            redirectUrl
            id
            isRetired
            permissions {
              canDuplicate
              canEdit
              canRetire
              canViewRecordings
              canViewSnapshot
            }
            position
            results {
              conversionRate
              conversions
              value
              hasBestImprovement
              improvementFromControl
              trafficSplit
              visitors
            }
            resultsId
            retiredAt
            snapshotId
            thumbnailUrl
            screenshotUrl
            type
            variantName
            viewVariantUrl
            weight
            significance
            description
          }
          variantsCount
          significance
          originalId
        }
      }
    `,
  },
});

export const abTestResultDetailQuery = ({ id }) => ({
  ...abTestsFeatureKeys.abTestResultDetail({ id }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query AbTestResultDetail($id: Int!) {
        abTestResultDetail(id: $id) {
          autoReweight
          createdAt
          createdByUserId
          createdByUserName
          devices
          duration
          goal {
            id
            name
            historical
            hasValue
          }
          selectedGoal {
            id
            name
            hasValue
          }
          id
          matchingUrl
          name
          notes
          pageUrl
          permissions {
            canAddVariants
            canDuplicate
            canAdjustWeight
            canChangeGoal
            canRename
            canDelete
            canStop
          }
          publishedAt
          publishedBy {
            name
          }
          siteId
          status
          stoppedAt
          type
          variants {
            createdAt
            updatedAt
            editorPatch
            redirectUrl
            id
            isRetired
            permissions {
              canDuplicate
              canEdit
              canRetire
              canViewSnapshot
            }
            position
            resultsId
            retiredAt
            snapshotId
            screenshotUrl
            type
            variantName
            viewVariantUrl
            weight
            description
          }
        }
      }
    `,
  },
});

export const abTestResultCountsQuery = ({ id, goalId, presetDate, startDate, endDate, order, sort }) => ({
  ...abTestsFeatureKeys.abTestResultCounts({
    id,
    goalId,
    presetDate,
    startDate,
    endDate,
    order: { field: order, sort },
  }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query AbTestResultCounts(
        $id: Int!
        $goalId: Int!
        $startDate: Int
        $endDate: Int
        $presetDate: String
        $order: AbTestVariantOrder
      ) {
        abTestResultCounts(
          id: $id
          goalId: $goalId
          startDate: $startDate
          endDate: $endDate
          presetDate: $presetDate
          order: $order
        ) {
          selectedGoal {
            id
            name
            hasValue
          }
          totals {
            totalVisitors
            totalConversions
            avgConversionRate
            totalValue
            significance
            hasResults
          }
          variantCounts {
            variantId
            visitors
            conversions
            conversionRate
            improvementFromControl
            value
            trafficSplit
            significance
            hasBestImprovement
          }
        }
      }
    `,
  },
});

export const abTestDistributionQuery = ({ id, goalId, presetDate, startDate, endDate }) => ({
  ...abTestsFeatureKeys.abTestDistribution({ id, goalId, presetDate, startDate, endDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query AbTestDistribution($id: Int!, $goalId: Int!, $startDate: Int, $endDate: Int, $presetDate: String) {
        abTestDistribution(
          id: $id
          goalId: $goalId
          startDate: $startDate
          endDate: $endDate
          presetDate: $presetDate
        ) {
          date
          startDate
          endDate
          granularity
          distribution {
            variantId
            totalConversions
            totalVisitors
            totalValue
            avgConversionRate
          }
        }
      }
    `,
  },
});
